import { Tags } from "@/enum/tags";
import type { SituationStatus } from "@/@types/unlock-request";
import { apiSlice } from "./http";

// ---------------------------------------------------------------------------
// DTOs — Operadores
// ---------------------------------------------------------------------------

export interface IStoreOperatorRecord {
  cpf: string | null;
  storeCode: number;
  operatorCode: string;
  operatorName: string;
  operatorType: string | null;
  classification: string;
  blocked: boolean;
  createdAt: string | null;
  blockedAt: string | null;
  situation: string | null;
  situationStatus: SituationStatus;
}

export interface IStoreOperatorsQueryParams {
  storeCode: number;
  search?: string;
  code?: string;
  classification?: string;
  cpfFilter?: "valid" | "invalid" | "all";
  blocked?: boolean | null;
  situation?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
}

export interface IStoreOperatorsResponse {
  message: string;
  data: IStoreOperatorRecord[];
  meta: {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
    prev: number | null;
    next: number | null;
  };
}

// ---------------------------------------------------------------------------
// DTOs — Usuários
// ---------------------------------------------------------------------------

export interface IStoreUserRecord {
  cpf: string | null;
  storeCode: number;
  userCode: number;
  userName: string;
  departmentId: number | null;
  employeeTypeId: number | null;
  classification: string;
  blocked: boolean;
  createdAt: string | null;
  blockedAt: string | null;
  situation: string | null;
  situationStatus: SituationStatus;
}

export interface IStoreUsersQueryParams {
  storeCode: number;
  search?: string;
  code?: string;
  classification?: string;
  cpfFilter?: "valid" | "invalid" | "all";
  blocked?: boolean | null;
  situation?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
}

export interface IStoreUsersResponse {
  message: string;
  data: IStoreUserRecord[];
  meta: {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
    prev: number | null;
    next: number | null;
  };
}

// ---------------------------------------------------------------------------
// Mutations + Export + Audit
// ---------------------------------------------------------------------------

export interface IOperatorBlockParams {
  storeCode: number;
  operatorCode: string;
  blocked: boolean;
}

export interface IUserBlockParams {
  storeCode: number;
  userCode: number;
  blocked: boolean;
}

export interface IPasswordResetParams {
  storeCode: number;
  operatorCode: string;
}

export interface IUserPasswordResetParams {
  storeCode: number;
  userCode: number;
}

export interface IExportExcelParams {
  storeCode: number;
  entity: "operators" | "users";
  search?: string;
  code?: string;
  classification?: string;
  cpfFilter?: "valid" | "invalid" | "all";
  blocked?: boolean | null;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
  analystName: string;
}

export interface IMutationResponse {
  message: string;
}

export interface IAuditHistoryRecord {
  id: number;
  entity_name: string;
  entity_id: string;
  ip: string;
  email: string;
  url: string;
  information: string;
  action: string;
  status: string;
  method: string;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildQueryString(params: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "") continue;
    qs.set(key, String(value));
  }
  return qs.toString();
}

// ---------------------------------------------------------------------------
// API Slice
// ---------------------------------------------------------------------------

export const storeAccessQueryApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getStoreOperators: builder.query<
      IStoreOperatorsResponse,
      IStoreOperatorsQueryParams
    >({
      query: ({ storeCode, ...params }) => {
        const qs = buildQueryString(params);
        return {
          url: `/access-automation/store/${storeCode}/operators${qs ? `?${qs}` : ""}`,
          method: "GET",
        };
      },
      providesTags: (_result, _error, { storeCode }) => [
        { type: Tags.STORE_OPERATORS_ACCESS, id: storeCode },
        Tags.STORE_OPERATORS_ACCESS,
      ],
    }),

    getStoreUsers: builder.query<IStoreUsersResponse, IStoreUsersQueryParams>({
      query: ({ storeCode, ...params }) => {
        const qs = buildQueryString(params);
        return {
          url: `/access-automation/store/${storeCode}/users${qs ? `?${qs}` : ""}`,
          method: "GET",
        };
      },
      providesTags: (_result, _error, { storeCode }) => [
        { type: Tags.STORE_USERS_ACCESS, id: storeCode },
        Tags.STORE_USERS_ACCESS,
      ],
    }),

    toggleOperatorBlock: builder.mutation<
      IMutationResponse,
      IOperatorBlockParams
    >({
      query: body => ({
        url: "/access-automation/operator/block",
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { storeCode }) => [
        { type: Tags.STORE_OPERATORS_ACCESS, id: storeCode },
        Tags.STORE_OPERATORS_ACCESS,
      ],
    }),

    toggleUserBlock: builder.mutation<IMutationResponse, IUserBlockParams>({
      query: body => ({
        url: "/access-automation/user/block",
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { storeCode }) => [
        { type: Tags.STORE_USERS_ACCESS, id: storeCode },
        Tags.STORE_USERS_ACCESS,
      ],
    }),

    resetOperatorPassword: builder.mutation<
      IMutationResponse,
      IPasswordResetParams
    >({
      query: body => ({
        url: "/access-automation/operator/reset-password",
        method: "PATCH",
        body,
      }),
    }),

    resetUserPassword: builder.mutation<
      IMutationResponse,
      IUserPasswordResetParams
    >({
      query: body => ({
        url: "/access-automation/user/reset-password",
        method: "PATCH",
        body,
      }),
    }),

    exportStoreAccess: builder.mutation<Blob, IExportExcelParams>({
      query: ({ storeCode, ...params }) => ({
        url: `/access-automation/store/${storeCode}/export`,
        method: "POST",
        body: params,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    getAuditHistory: builder.query<IAuditHistoryRecord[], string[]>({
      query: entityIds => ({
        url: `/access-automation/audit-history?entityIds=${entityIds.join(",")}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetStoreOperatorsQuery,
  useGetStoreUsersQuery,
  useToggleOperatorBlockMutation,
  useToggleUserBlockMutation,
  useResetOperatorPasswordMutation,
  useResetUserPasswordMutation,
  useExportStoreAccessMutation,
  useLazyGetAuditHistoryQuery,
} = storeAccessQueryApi;
